import { useMemo, useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { ChevronDown, SlidersHorizontal, X } from 'lucide-react'
import {
  categories,
  shopBrowseCategories,
  productsShownInShop,
  type Category,
  type CategoryInfo,
  type Product,
} from '#bv/data/products'
import { productsShownMerged } from '#bv/lib/productCatalog'
import { filterShopProducts } from '#bv/lib/shopSearch'
import { ProductCard } from '#bv/components/ProductCard'
import { FeatureIcon } from '#bv/components/FeatureIcons'
import { newUiEnabled } from '#bv/newui/newUiFlag'

export type ShopCatalogInitial = {
  q?: string
  category?: string
  price?: string
  sort?: string
}

type ShopCatalogProps = {
  variant: 'shop' | 'preview'
  initial?: ShopCatalogInitial
  /** Live shop: merged catalog from loader (includes admin overrides). */
  catalog?: Product[]
}

const PRICE_FILTERS = [
  { id: 'under15', label: 'Under $15', match: (p: Product) => p.price < 15 },
  { id: '15-35', label: '$15 – $35', match: (p: Product) => p.price >= 15 && p.price < 35 },
  { id: '35-60', label: '$35 – $60', match: (p: Product) => p.price >= 35 && p.price < 60 },
  { id: 'over60', label: '$60+', match: (p: Product) => p.price >= 60 },
] as const

const VALID_PRICE_IDS = new Set(PRICE_FILTERS.map((f) => f.id))

function parsePriceBuckets(raw?: string): Set<string> {
  const out = new Set<string>()
  if (!raw?.trim()) return out
  for (const part of raw.split(',')) {
    const id = part.trim()
    if (VALID_PRICE_IDS.has(id)) out.add(id)
  }
  return out
}

function initialCategory(raw?: string): Category | 'all' {
  if (!raw?.trim()) return 'all'
  const id = raw.trim() as Category
  return shopBrowseCategories.some((c) => c.id === id) ? id : 'all'
}

function tabShortLabel(cat: CategoryInfo): string {
  const map: Record<Category, string> = {
    discord: 'Discord',
    streaming: 'Streaming',
    gaming: 'Gaming',
    social: 'Social',
    software: 'Software',
    zenith_lifetime: 'Zenith Lifetime',
    zenith_monthly: 'Zenith Monthly',
    alchemy_lifetime: 'Alchemy Lifetime',
    alchemy_token: 'Alchemy Token',
    alchemy_30_days: 'Alchemy 30 Days',
  }
  return map[cat.id] ?? cat.label
}

function priceBucketsKey(buckets: Set<string>): string {
  return [...buckets].sort().join(',')
}

function shopUrlSearch(initial?: ShopCatalogInitial): ShopCatalogInitial {
  return {
    q: initial?.q ?? '',
    category: initial?.category ?? '',
    price: initial?.price ?? '',
    sort: initial?.sort ?? 'default',
  }
}

function shopUrlSearchChanged(a?: ShopCatalogInitial, b?: ShopCatalogInitial): boolean {
  const left = shopUrlSearch(a)
  const right = shopUrlSearch(b)
  return (
    left.q !== right.q ||
    left.category !== right.category ||
    left.price !== right.price ||
    left.sort !== right.sort
  )
}

function filtersMatchUrl(
  query: string,
  activeCategory: Category | 'all',
  priceBuckets: Set<string>,
  sort: string,
  initial?: ShopCatalogInitial,
): boolean {
  return (
    query.trim() === (initial?.q ?? '').trim() &&
    activeCategory === initialCategory(initial?.category) &&
    priceBucketsKey(priceBuckets) === priceBucketsKey(parsePriceBuckets(initial?.price)) &&
    sort === (initial?.sort ?? 'default')
  )
}

export function ShopCatalog({ variant, initial, catalog }: ShopCatalogProps) {
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [query, setQuery] = useState(initial?.q ?? '')
  const [activeCategory, setActiveCategory] = useState<Category | 'all'>(() => initialCategory(initial?.category))
  const [priceBuckets, setPriceBuckets] = useState<Set<string>>(() => parsePriceBuckets(initial?.price))
  const [sort, setSort] = useState(initial?.sort ?? 'default')
  const isPreview = variant === 'preview'
  const nv = newUiEnabled() && variant === 'shop'

  const storefrontProducts = useMemo((): Product[] => {
    if (variant === 'shop' && catalog?.length) return productsShownMerged(catalog)
    return productsShownInShop()
  }, [variant, catalog])

  const prevUrlSearchRef = useRef<ShopCatalogInitial | undefined>(initial)

  useEffect(() => {
    if (variant !== 'shop') return

    const urlChanged = shopUrlSearchChanged(prevUrlSearchRef.current, initial)

    if (urlChanged) {
      prevUrlSearchRef.current = initial
      setQuery(initial?.q ?? '')
      setActiveCategory(initialCategory(initial?.category))
      setPriceBuckets(parsePriceBuckets(initial?.price))
      setSort(initial?.sort ?? 'default')
      return
    }

    if (pathname !== '/shop') return
    if (filtersMatchUrl(query, activeCategory, priceBuckets, sort, initial)) return

    const priceStr = priceBucketsKey(priceBuckets) || undefined
    const nextSearch: ShopCatalogInitial = {
      q: query.trim() || undefined,
      category: activeCategory === 'all' ? undefined : activeCategory,
      price: priceStr,
      sort: sort === 'default' ? undefined : sort,
    }

    prevUrlSearchRef.current = nextSearch
    navigate({
      to: '/shop',
      search: nextSearch,
      replace: true,
      resetScroll: false,
    })
  }, [variant, pathname, query, activeCategory, priceBuckets, sort, initial, navigate])

  const togglePriceBucket = (id: string) => {
    setPriceBuckets((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const resetFilters = () => {
    setQuery('')
    setActiveCategory('all')
    setPriceBuckets(new Set())
    setSort('default')
  }

  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false)

  useEffect(() => {
    if (!filtersDrawerOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFiltersDrawerOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [filtersDrawerOpen])

  const filtered = useMemo(() => {
    let list = [...storefrontProducts]
    if (activeCategory !== 'all') {
      list = list.filter((p) => p.category === activeCategory)
    }
    if (priceBuckets.size > 0) {
      list = list.filter((p) => PRICE_FILTERS.some((f) => priceBuckets.has(f.id) && f.match(p)))
    }
    if (query.trim() && catalog?.length) {
      list = filterShopProducts(list, catalog, query)
    } else if (query.trim()) {
      const q = query.toLowerCase()
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.shortDescription.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q),
      )
    }
    switch (sort) {
      case 'price-asc':
        list.sort((a, b) => a.price - b.price)
        break
      case 'price-desc':
        list.sort((a, b) => b.price - a.price)
        break
      case 'rating':
        list.sort((a, b) => b.rating - a.rating)
        break
      case 'popular':
        list.sort((a, b) => b.reviewCount - a.reviewCount)
        break
    }
    return list
  }, [storefrontProducts, catalog, query, activeCategory, priceBuckets, sort])

  const activeChips: { key: string; label: string; onRemove: () => void }[] = []
  if (query.trim()) {
    activeChips.push({
      key: 'q',
      label: `Search: ${query.trim()}`,
      onRemove: () => setQuery(''),
    })
  }
  if (activeCategory !== 'all') {
    const lab = categories.find((c) => c.id === activeCategory)?.label ?? activeCategory
    activeChips.push({
      key: 'cat',
      label: lab,
      onRemove: () => setActiveCategory('all'),
    })
  }
  for (const id of priceBuckets) {
    const lab = PRICE_FILTERS.find((f) => f.id === id)?.label ?? id
    activeChips.push({
      key: `p-${id}`,
      label: lab,
      onRemove: () => setPriceBuckets((prev) => {
        const n = new Set(prev)
        n.delete(id)
        return n
      }),
    })
  }

  const hasActiveFilters =
    query.trim().length > 0 || activeCategory !== 'all' || priceBuckets.size > 0 || sort !== 'default'

  const filterChipCount = activeChips.length

  const filtersCore = (
    <div className={nv ? 'nv-vault-filters' : 'space-y-5'}>
      <div className="relative">
        <svg
          className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#6a4a62"
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search Pulse Shop..."
          className={nv ? 'nv-vault-search w-full' : 'bv-input w-full rounded-xl py-3 pr-3 pl-11 text-sm'}
        />
      </div>

      {activeChips.length > 0 ? (
        <div>
          <p className="mb-2 text-[0.65rem] font-semibold uppercase tracking-wider" style={{ color: '#5a6d8a' }}>
            Active
          </p>
          <div className="flex flex-wrap gap-2">
            {activeChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={chip.onRemove}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[0.7rem] font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00bfff]/35"
                style={{
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.25))',
                  border: '1px solid rgba(255,255,255,0.08)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
                  color: '#a8c4e8',
                }}
              >
                <span className="max-w-[200px] truncate sm:max-w-[220px]">{chip.label}</span>
                <span className="text-[#5a6d8a]" aria-hidden>
                  ×
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <details open className="group border-t border-white/[0.06] pt-4">
        <summary className="flex cursor-pointer list-none items-center justify-between py-1 text-sm font-semibold text-[#f0f6ff] marker:hidden [&::-webkit-details-marker]:hidden">
          <span>Price</span>
          <ChevronDown
            className="h-4 w-4 shrink-0 text-[#5a6d8a] transition-transform duration-200 group-open:rotate-180"
            aria-hidden
          />
        </summary>
        <div className="mt-3 space-y-1">
          {PRICE_FILTERS.map((f) => {
            const on = priceBuckets.has(f.id)
            return (
              <label
                key={f.id}
                className={`flex cursor-pointer items-center gap-3 rounded-xl border px-2.5 py-2.5 text-sm transition-colors hover:bg-white/[0.04] ${
                  on
                    ? 'border-[rgba(0, 191, 255,0.35)] bg-[rgba(0, 191, 255,0.08)]'
                    : 'border-transparent hover:border-white/[0.06]'
                }`}
                style={{ color: '#a8c4e8' }}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => togglePriceBucket(f.id)}
                  className="h-4 w-4 shrink-0 rounded border-white/25 accent-[#00bfff]"
                />
                <span className="leading-tight">{f.label}</span>
              </label>
            )
          })}
        </div>
      </details>

      <p className="m-0 rounded-lg border border-dashed border-white/[0.08] px-3 py-2.5 text-[0.65rem] leading-relaxed" style={{ color: '#5a6d8a' }}>
        Use category tabs with price ranges to narrow the grid — everything stays in sync with the URL on the live shop.
      </p>
    </div>
  )

  return (
    <div className={nv ? 'nv-vault' : 'min-h-screen'} style={nv ? undefined : { background: 'var(--black)', color: 'var(--text)' }}>
      {nv ? (
        <section className="nv-vault-hero">
          <div className="nv-vault-hero-ambient" aria-hidden />
          <div className="nv-wrap">
            <nav className="nv-vault-crumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden>·</span>
              <span className="nv-vault-crumb-current">Pulse Shop</span>
            </nav>
            <p className="nv-vault-kicker">Curated catalog</p>
            <h1 className="nv-vault-title">
              Browse <span className="nv-vault-title-grad">premium listings</span>
            </h1>
            <p className="nv-vault-lede">
              {storefrontProducts.length} digital goods — filter by category, price, and sort to find your deal fast.
            </p>
            <div className="nv-vault-meta">
              <span className="nv-vault-count">
                <strong className="tabular-nums">{filtered.length}</strong>
                <span className="nv-vault-count-sep">/</span>
                <span className="tabular-nums">{storefrontProducts.length}</span>
                <span className="nv-vault-count-label">showing</span>
              </span>
            </div>
            <div className="nv-vault-note nv-vault-disclaimer" role="note" aria-label="Shared access disclaimer">
              <div className="nv-vault-disclaimer-inner">
                <div className="nv-vault-disclaimer-kicker">
                  <span className="nv-vault-disclaimer-ic" aria-hidden />
                  Shared access notice
                </div>
                <div className="nv-vault-disclaimer-text">
                  Shared Access listings use managed logins — access lasts while the account stays active. Review each product
                  page for terms.
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <div
          className="border-b border-white/[0.07]"
          style={{
            background:
              'linear-gradient(180deg, rgba(0, 191, 255,0.08) 0%, rgba(7,5,8,0.4) 55%, transparent 100%), radial-gradient(800px 280px at 18% 0%, rgba(255,105,185,0.06), transparent 55%)',
          }}
        >
          <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-12 lg:px-8">
            <nav className="mb-5 text-[0.7rem] font-medium uppercase tracking-[0.2em]" style={{ color: '#5a6d8a' }}>
              <Link to="/" className="transition-colors hover:text-[#d4e8ff]">
                Home
              </Link>
              <span className="mx-2.5 text-[#5a6d8a]" aria-hidden>
                ·
              </span>
              <span className="text-[#f0f6ff]">Pulse Shop</span>
            </nav>
            <h1 className="font-display text-5xl tracking-wide sm:text-6xl lg:text-7xl chrome-text">
              {isPreview ? 'PRODUCT PREVIEW' : 'Pulse Shop'}
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed sm:text-base" style={{ color: '#5a6d8a' }}>
              {isPreview
                ? 'Same cards and filters as the live shop — use this to review layout and imagery before publishing.'
                : `${storefrontProducts.length} premium digital listings — pick a category, tune price filters, and grab a deal.`}
            </p>
            {isPreview ? null : (
              <p className="mt-3 max-w-3xl text-xs leading-relaxed" style={{ color: '#5a6d8a' }}>
                Note: listings labeled “Shared Access” use managed logins — access lasts while the account stays active and is not
                guaranteed forever. Misuse (password/email changes, verification triggers) can end access early.
              </p>
            )}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span
                className="inline-flex items-center rounded-full border border-white/[0.1] px-3 py-1.5 text-[0.72rem] font-medium shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
                style={{ background: 'rgba(0,0,0,0.35)', color: '#5a6d8a' }}
              >
                <span className="tabular-nums font-semibold text-[#d4e8ff]">{filtered.length}</span>
                <span className="mx-1.5 text-[#5a6d8a]" aria-hidden>
                  /
                </span>
                <span className="tabular-nums text-[#8ecfff]">{storefrontProducts.length}</span>
                <span className="ml-2 uppercase tracking-wider">listings</span>
              </span>
            </div>
          </div>
        </div>
      )}

      <div
        className={
          nv
            ? 'nv-wrap nv-vault-layout'
            : 'mx-auto flex max-w-7xl flex-col gap-8 px-4 py-8 sm:px-6 lg:grid lg:grid-cols-[minmax(0,272px)_1fr] lg:items-start lg:gap-x-10 lg:gap-y-0 lg:px-8 lg:py-10'
        }
      >
        {/* Sidebar filters — desktop only; mobile uses sheet below */}
        <aside className={nv ? 'nv-vault-sidebar' : 'hidden w-full shrink-0 rounded-2xl border border-white/[0.09] p-5 shadow-[0_12px_48px_rgba(0,0,0,0.35)] ring-1 ring-white/[0.03] lg:block lg:sticky lg:top-24 lg:self-start'} style={nv ? undefined : { background: 'linear-gradient(165deg, rgba(26,22,30,0.98) 0%, #141218 100%)' }}>
          <div className={nv ? 'nv-vault-sidebar-head' : 'flex items-center justify-between gap-3 border-b border-white/[0.06] pb-4'}>
            <h2 className={nv ? 'nv-vault-sidebar-title' : 'm-0 text-[0.65rem] font-bold uppercase tracking-[0.22em]'} style={nv ? undefined : { color: '#5a6d8a' }}>
              Filters
            </h2>
            <button
              type="button"
              onClick={resetFilters}
              disabled={!hasActiveFilters}
              className={
                nv
                  ? 'nv-vault-reset'
                  : 'rounded-lg border border-transparent px-3 py-1.5 text-[0.7rem] font-semibold transition-all hover:border-[rgba(0, 191, 255,0.35)] hover:bg-[rgba(0, 191, 255,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00bfff]/35 disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-transparent disabled:hover:bg-transparent'
              }
              style={nv ? undefined : { color: '#93c5fd' }}
            >
              Reset
            </button>
          </div>

          <div className={nv ? 'nv-vault-sidebar-body' : 'mt-5'}>{filtersCore}</div>
        </aside>

        {/* Main */}
        <div className={nv ? 'nv-vault-main' : 'min-w-0 lg:min-h-[200px]'}>
          <div className={nv ? 'nv-vault-toolbar-mobile' : 'mb-4 flex items-stretch gap-2 lg:hidden'}>
            <button
              type="button"
              onClick={() => setFiltersDrawerOpen(true)}
              className={
                nv
                  ? 'nv-vault-filter-btn'
                  : 'inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-white/[0.1] py-3.5 text-sm font-semibold text-[#f0f6ff] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition-colors hover:border-[rgba(0, 191, 255,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00bfff]/35'
              }
              style={nv ? undefined : { background: 'rgba(0,0,0,0.45)' }}
              aria-haspopup="dialog"
              aria-expanded={filtersDrawerOpen}
            >
              <SlidersHorizontal className="h-4 w-4 shrink-0 text-[#93c5fd]" aria-hidden />
              Filters
              {filterChipCount > 0 ? (
                <span className="pd-grad-bg ml-0.5 min-w-[1.25rem] rounded-full px-1.5 py-0.5 text-center text-[0.65rem] font-bold tabular-nums text-white">
                  {filterChipCount}
                </span>
              ) : null}
            </button>
          </div>

          <div className={nv ? 'nv-vault-toolbar' : 'mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6'}>
            <div className="min-w-0 flex-1">
              <div className={nv ? 'nv-vault-tabs-wrap' : undefined}>
                <div className={nv ? 'nv-vault-tabs' : 'flex flex-wrap gap-1 rounded-2xl p-1 ring-1 ring-white/[0.06]'} style={nv ? undefined : { background: 'rgba(0,0,0,0.45)' }}>
                <button
                  type="button"
                  onClick={() => setActiveCategory('all')}
                  className={
                    nv
                      ? `nv-vault-tab${activeCategory === 'all' ? ' nv-vault-tab--on' : ''}`
                      : `shrink-0 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00bfff]/35 ${
                          activeCategory === 'all'
                            ? 'text-[#f0f6ff] shadow-[inset_0_0_0_1px_rgba(0, 191, 255,0.35)]'
                            : 'text-[#5a6d8a] hover:bg-white/[0.05] hover:text-[#8ecfff]'
                        }`
                  }
                  style={!nv && activeCategory === 'all' ? { background: 'rgba(0, 191, 255,0.18)' } : undefined}
                >
                  All
                </button>
                {shopBrowseCategories.map((cat) => {
                  const on = activeCategory === cat.id
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setActiveCategory(cat.id)}
                      className={
                        nv
                          ? `nv-vault-tab${on ? ' nv-vault-tab--on' : ''}`
                          : `shrink-0 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00bfff]/35 ${
                              on
                                ? 'text-[#f0f6ff] shadow-[inset_0_0_0_1px_rgba(0, 191, 255,0.35)]'
                                : 'text-[#5a6d8a] hover:bg-white/[0.05] hover:text-[#8ecfff]'
                            }`
                      }
                      style={!nv && on ? { background: 'rgba(0, 191, 255,0.18)' } : undefined}
                    >
                      {tabShortLabel(cat)}
                    </button>
                  )
                })}
                </div>
              </div>
            </div>
            <div className={nv ? 'nv-vault-sort-row' : 'flex shrink-0 items-center gap-2 sm:pl-1'}>
              <SlidersHorizontal className="h-4 w-4 shrink-0 text-[#5a6d8a]" aria-hidden />
              <div className="relative min-w-[12rem]">
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className={
                    nv
                      ? 'nv-vault-sort'
                      : 'bv-input min-h-[2.75rem] w-full cursor-pointer rounded-xl py-2.5 pr-10 pl-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00bfff]/35'
                  }
                  style={nv ? { appearance: 'none' } : { appearance: 'none' }}
                  aria-label="Sort products"
                >
                  <option value="default">Recommended</option>
                  <option value="rating">Top rated</option>
                  <option value="popular">Most popular</option>
                  <option value="price-asc">Price: Low to high</option>
                  <option value="price-desc">Price: High to low</option>
                </select>
                <ChevronDown
                  className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-[#5a6d8a]"
                  aria-hidden
                />
              </div>
            </div>
          </div>

          <div className={nv ? 'nv-vault-catalog-head' : 'mb-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-white/[0.06] pb-5'}>
            <h2 className={nv ? 'nv-vault-catalog-title' : 'm-0 text-[0.7rem] font-bold uppercase tracking-[0.22em]'} style={nv ? undefined : { color: '#5a6d8a' }}>
              Browse catalog
            </h2>
            <p className={nv ? 'nv-vault-catalog-meta' : 'm-0 text-right text-sm leading-snug'} style={nv ? undefined : { color: '#5a6d8a' }}>
              <span className="tabular-nums font-semibold text-[#d4e8ff]">{filtered.length}</span>
              <span> matching</span>
              {activeCategory !== 'all' ? (
                <>
                  <span className="text-[#5a6d8a]"> · </span>
                  <span className="font-medium text-[#93c5fd]">{categories.find((c) => c.id === activeCategory)?.label}</span>
                </>
              ) : null}
            </p>
          </div>

          {filtered.length > 0 ? (
            <div className={nv ? 'nv-vault-showcase' : undefined}>
              <div className={nv ? 'nv-vault-grid' : 'grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-7 lg:grid-cols-3 [&>*]:min-h-0'}>
                {filtered.map((product) => (
                  <ProductCard key={product.id} product={product} variant="vault" />
                ))}
              </div>
            </div>
          ) : (
            <div className={nv ? 'nv-vault-empty' : 'surface-panel rounded-2xl py-20 text-center shadow-[0_12px_48px_rgba(0,0,0,0.3)]'}>
              <div className="mb-4 flex justify-center">
                <FeatureIcon name="search" size={44} strokeWidth={1.75} className="text-[#8ecfff]" />
              </div>
              <h3 className={nv ? 'nv-vault-empty-title' : 'font-display mb-2 text-3xl tracking-wide chrome-text'}>No results</h3>
              <p className={nv ? 'nv-vault-empty-copy' : 'mx-auto mb-6 max-w-md px-4 text-sm'} style={nv ? undefined : { color: '#5a6d8a' }}>
                Nothing matched these filters. Try clearing price checkboxes, switching category, or resetting search.
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className={nv ? 'nv-btn nv-btn-primary' : 'btn-chrome rounded-xl px-6 py-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00bfff]/35'}
              >
                Reset filters
              </button>
            </div>
          )}

          {isPreview && (
            <p className="mt-10 text-center text-xs" style={{ color: '#5a6d8a' }}>
              Live catalog:{' '}
              <Link to="/shop" className="font-medium underline-offset-2 hover:underline" style={{ color: '#8ecfff' }}>
                Open the real shop
              </Link>
              . Enable on production with env{' '}
              <code className="rounded bg-white/5 px-1.5 py-0.5" style={{ color: '#93c5fd' }}>
                VITE_PREVIEW_PRODUCTS=true
              </code>
              .
            </p>
          )}
        </div>
      </div>

      {filtersDrawerOpen ? (
        <div
          className={nv ? 'nv-vault-drawer' : 'fixed inset-0 z-[100] lg:hidden'}
          role="dialog"
          aria-modal="true"
          aria-labelledby="vault-filters-sheet-title"
        >
          <button
            type="button"
            className={nv ? 'nv-vault-drawer-backdrop' : 'absolute inset-0 border-0 bg-black/75 p-0'}
            aria-label="Close filters"
            onClick={() => setFiltersDrawerOpen(false)}
          />
          <div
            className={
              nv
                ? 'nv-vault-drawer-panel'
                : 'absolute inset-x-0 bottom-0 flex max-h-[min(90dvh,780px)] flex-col rounded-t-3xl border border-white/[0.1] shadow-[0_-20px_60px_rgba(0,0,0,0.55)] ring-1 ring-white/[0.04]'
            }
            style={nv ? undefined : { background: 'linear-gradient(165deg, rgba(26,22,30,0.99) 0%, #141218 100%)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="mx-auto mt-3 h-1 w-10 shrink-0 rounded-full bg-white/20"
              aria-hidden
            />
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/[0.08] px-4 pb-3 pt-2">
              <h2
                id="vault-filters-sheet-title"
                className="m-0 text-[0.65rem] font-bold uppercase tracking-[0.22em]"
                style={{ color: '#5a6d8a' }}
              >
                Filters
              </h2>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={resetFilters}
                  disabled={!hasActiveFilters}
                  className="rounded-lg border border-transparent px-3 py-2 text-[0.7rem] font-semibold transition-all hover:border-[rgba(0, 191, 255,0.35)] hover:bg-[rgba(0, 191, 255,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00bfff]/35 disabled:cursor-not-allowed disabled:opacity-35"
                  style={{ color: '#93c5fd' }}
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={() => setFiltersDrawerOpen(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/[0.08] text-[#8ecfff] transition-colors hover:border-[rgba(0, 191, 255,0.35)] hover:bg-white/[0.05] hover:text-[#d4e8ff] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00bfff]/35"
                  aria-label="Close filters"
                >
                  <X className="h-5 w-5" strokeWidth={2} />
                </button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4">{filtersCore}</div>
            <div className={nv ? 'nv-vault-drawer-foot' : 'shrink-0 border-t border-white/[0.08] p-4 pb-[max(1rem,env(safe-area-inset-bottom))]'}>
              <button
                type="button"
                onClick={() => setFiltersDrawerOpen(false)}
                className={
                  nv
                    ? 'nv-btn nv-btn-primary w-full'
                    : 'btn-chrome w-full rounded-xl py-3.5 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00bfff]/35'
                }
              >
                Show {filtered.length} {filtered.length === 1 ? 'result' : 'results'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
